import {
  RACE_RUN_DOCUMENT_COLUMNS,
  RACE_RUN_INDEX_COLUMNS,
  fromRaceRunRow,
  toRaceRunRow,
  topRaceRunIds,
  type HighScore,
  type RaceRun,
} from '../../sim/highScores/index.ts';
import { getSupabaseClient } from './supabaseClient.ts';

const TABLE = 'race_runs';
/** Supabase's default PostgREST row cap; a shorter page means the table is exhausted. */
const PAGE_SIZE = 1000;
const IDS_PER_REQUEST = 100;
/** The start screen waits on this fetch; past it, Top lists mount from the local cache. */
const FETCH_TIMEOUT_MS = 4000;

function rig(): string {
  return import.meta.env.VITE_HELMWAY_RIG?.trim() ?? '';
}

function channel(): string {
  return (
    import.meta.env.VITE_HELMWAY_CHANNEL?.trim() || (import.meta.env.DEV ? 'dev' : 'build')
  );
}

/**
 * The best `HIGH_SCORE_LIMIT` runs per map, migrated, or null when there is no client or the
 * request failed. Ranks every row by its integer columns first, then downloads only the
 * documents (with replays) that can reach a Top list.
 */
export async function fetchRaceRuns(): Promise<RaceRun[] | null> {
  const supabase = getSupabaseClient();

  if (!supabase) {
    return null;
  }

  const signal = AbortSignal.timeout(FETCH_TIMEOUT_MS);

  try {
    const index: unknown[] = [];

    for (let from = 0; ; from += PAGE_SIZE) {
      const { data, error } = await supabase
        .from(TABLE)
        .select(RACE_RUN_INDEX_COLUMNS)
        .order('id')
        .range(from, from + PAGE_SIZE - 1)
        .abortSignal(signal);

      if (error) {
        return null;
      }

      index.push(...(data as unknown[]));

      if (data.length < PAGE_SIZE) {
        break;
      }
    }

    const ids = topRaceRunIds(index);
    const runs: RaceRun[] = [];

    for (let start = 0; start < ids.length; start += IDS_PER_REQUEST) {
      const { data, error } = await supabase
        .from(TABLE)
        .select(RACE_RUN_DOCUMENT_COLUMNS)
        .in('id', ids.slice(start, start + IDS_PER_REQUEST))
        .abortSignal(signal);

      if (error) {
        return null;
      }

      for (const row of data as unknown[]) {
        const run = fromRaceRunRow(row);

        if (run) {
          runs.push(run);
        }
      }
    }

    return runs;
  } catch {
    return null;
  }
}

/** Stores each finish in Helmway. True once the table has every row; failures are not retried. */
export async function upsertRaceRuns(
  mapId: string,
  scores: readonly HighScore[],
): Promise<boolean> {
  const supabase = getSupabaseClient();

  if (!supabase) {
    return false;
  }

  if (scores.length === 0) {
    return true;
  }

  const rows = scores.map((score) => toRaceRunRow(score, mapId, rig(), channel()));

  try {
    // The browser role has no UPDATE grant, so a merge upsert is refused; a finish already
    // stored is skipped instead.
    const { error } = await supabase
      .from(TABLE)
      .upsert(rows, { onConflict: 'map_id,recorded_at,boat_id', ignoreDuplicates: true });
    return !error;
  } catch {
    return false;
  }
}
